import useTranslate from "@hooks/use_translate"
import CallSplitIcon from "@mui/icons-material/CallSplit"
import CompareArrowsIcon from "@mui/icons-material/CompareArrows"
import DataObjectIcon from "@mui/icons-material/DataObject"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import DifferenceOutlinedIcon from "@mui/icons-material/DifferenceOutlined"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import HistoryIcon from "@mui/icons-material/History"
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined"
import MoreVertIcon from "@mui/icons-material/MoreVert"
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined"
import ScienceIcon from "@mui/icons-material/Science"
import SearchIcon from "@mui/icons-material/Search"
import LoadingButton from "@mui/lab/LoadingButton"
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tooltip
} from "@mui/material"
import type { IAiPrompts, IAiPromptsInput } from "@pages/main/scripts/prompts"
import {
  promptLayerLabel,
  promptRowKey
} from "@pages/main/scripts/prompts/prompt.models"
import PromptDiff from "@pages/main/scripts/prompts/sub_components/PromptDiffs"
import PromptVersionDiff from "@pages/main/scripts/prompts/sub_components/PromptVersionDiffs"
import { useState } from "react"

type OpenDialog =
  | { kind: "history" }
  | { kind: "review" }
  | { kind: "other" }
  | { kind: "layer"; sibling: IAiPrompts }

interface PromptActionBarProps {
  selectedPrompt: IAiPrompts | null
  prompts: IAiPrompts[]
  layerSiblings: IAiPrompts[]
  draft: IAiPromptsInput | null
  forkTargets: string[]
  hasSchema: boolean
  isDirty: boolean
  canSave: boolean
  canPlay: boolean
  submitting: boolean
  deleting: boolean
  forking: boolean
  onOpenSchema: () => void
  onRemoveSchema: () => void
  onOpenPlayground: () => void
  onSave: () => void
  onDelete: () => void
  onFork: (target: string) => void
  onLoadVersion: (version: IAiPromptsInput) => void
  onRollbackVersion: (version: IAiPromptsInput, label: string) => void
}

/**
 * Left: read-only looks at the row (history, compare, schema) plus a menu for the rare,
 * destructive or copying actions. Right: the draft's own path — try it, review it, save it.
 */
export default function PromptActionBar({
  selectedPrompt,
  prompts,
  layerSiblings,
  draft,
  forkTargets,
  hasSchema,
  isDirty,
  canSave,
  canPlay,
  submitting,
  deleting,
  forking,
  onOpenSchema,
  onRemoveSchema,
  onOpenPlayground,
  onSave,
  onDelete,
  onFork,
  onLoadVersion,
  onRollbackVersion
}: PromptActionBarProps) {
  const { translate } = useTranslate()
  const [dialog, setDialog] = useState<OpenDialog | null>(null)
  const [compareAnchor, setCompareAnchor] = useState<HTMLElement | null>(null)
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null)
  const closeDialog = () => setDialog(null)
  const versionCount = selectedPrompt?.versions?.length ?? 0
  const busy = submitting || deleting

  const openFromMenu = (next: OpenDialog) => {
    setCompareAnchor(null)
    setDialog(next)
  }

  return (
    <Paper variant="outlined" sx={{ px: 1, py: 0.75, borderRadius: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        flexWrap="wrap"
        useFlexGap
      >
        {selectedPrompt && (
          <>
            <Tooltip title={translate("prompt_action_history_hint")}>
              <Button
                size="small"
                color="inherit"
                startIcon={<HistoryIcon fontSize="small" />}
                onClick={() => setDialog({ kind: "history" })}
              >
                {translate("prompt_action_history")}
                {versionCount > 0 && (
                  <Box
                    component="span"
                    sx={{ ml: 0.5, color: "text.secondary", fontWeight: 400 }}
                  >
                    {versionCount}
                  </Box>
                )}
              </Button>
            </Tooltip>
            <Button
              size="small"
              color="inherit"
              startIcon={<CompareArrowsIcon fontSize="small" />}
              endIcon={<ExpandMoreIcon fontSize="small" />}
              onClick={(event) => setCompareAnchor(event.currentTarget)}
            >
              {translate("prompt_action_compare")}
            </Button>
          </>
        )}
        <Tooltip
          title={translate(
            hasSchema
              ? "prompt_action_schema_set"
              : "prompt_action_schema_unset"
          )}
        >
          <Button
            size="small"
            color="inherit"
            startIcon={
              <Badge
                variant="dot"
                color="success"
                invisible={!hasSchema}
                overlap="circular"
              >
                <DataObjectIcon fontSize="small" />
              </Badge>
            }
            onClick={onOpenSchema}
          >
            {translate("prompt_action_schema")}
          </Button>
        </Tooltip>
        {selectedPrompt && (
          <Tooltip title={translate("prompt_action_more")}>
            <IconButton
              size="small"
              onClick={(event) => setMoreAnchor(event.currentTarget)}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Box sx={{ flex: 1 }} />

        <Button
          size="small"
          variant="outlined"
          startIcon={<ScienceIcon fontSize="small" />}
          disabled={!canPlay}
          onClick={onOpenPlayground}
        >
          Playground
        </Button>
        {selectedPrompt && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<DifferenceOutlinedIcon fontSize="small" />}
            disabled={!isDirty}
            onClick={() => setDialog({ kind: "review" })}
          >
            {translate("prompt_action_review")}
          </Button>
        )}
        <LoadingButton
          size="small"
          variant="contained"
          startIcon={<SaveOutlinedIcon fontSize="small" />}
          loading={submitting}
          disabled={!canSave || busy}
          onClick={onSave}
        >
          {translate(
            selectedPrompt ? "prompt_action_save" : "prompt_action_create"
          )}
        </LoadingButton>
      </Stack>

      <Menu
        anchorEl={compareAnchor}
        open={!!compareAnchor}
        onClose={() => setCompareAnchor(null)}
      >
        {layerSiblings.length > 0 && (
          <ListSubheader sx={{ lineHeight: "32px" }}>
            {translate("prompt_action_compare_layers")}
          </ListSubheader>
        )}
        {selectedPrompt &&
          layerSiblings.map((sibling) => (
            <MenuItem
              key={promptRowKey(sibling)}
              onClick={() => openFromMenu({ kind: "layer", sibling })}
            >
              <ListItemIcon>
                <LayersOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                {promptLayerLabel(selectedPrompt.engineVersion)} ↔{" "}
                {promptLayerLabel(sibling.engineVersion)}
              </ListItemText>
            </MenuItem>
          ))}
        {layerSiblings.length > 0 && <Divider />}
        <MenuItem onClick={() => openFromMenu({ kind: "other" })}>
          <ListItemIcon>
            <SearchIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {translate("prompt_action_compare_other")}
          </ListItemText>
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={moreAnchor}
        open={!!moreAnchor}
        onClose={() => setMoreAnchor(null)}
      >
        {forkTargets.length > 0 && (
          <ListSubheader sx={{ lineHeight: "32px" }}>
            {translate("prompt_action_fork_header")}
          </ListSubheader>
        )}
        {forkTargets.map((target) => (
          <MenuItem
            key={target}
            disabled={forking}
            onClick={() => {
              setMoreAnchor(null)
              onFork(target)
            }}
          >
            <ListItemIcon>
              <CallSplitIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={translate("prompt_action_fork_to", { target })}
              secondary={translate("prompt_action_fork_hint")}
            />
          </MenuItem>
        ))}
        {forkTargets.length > 0 && <Divider />}
        {hasSchema && (
          <MenuItem
            onClick={() => {
              setMoreAnchor(null)
              onRemoveSchema()
            }}
          >
            <ListItemIcon>
              <DataObjectIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {translate("prompt_action_remove_schema")}
            </ListItemText>
          </MenuItem>
        )}
        <MenuItem
          disabled={busy}
          onClick={() => {
            setMoreAnchor(null)
            onDelete()
          }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon sx={{ color: "inherit" }}>
            <DeleteOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{translate("prompt_action_delete")}</ListItemText>
        </MenuItem>
      </Menu>

      {selectedPrompt && (
        <>
          <PromptVersionDiff
            open={dialog?.kind === "history"}
            onClose={closeDialog}
            prompt={selectedPrompt}
            title={translate("prompt_action_history")}
            onLoadVersion={(version) => {
              closeDialog()
              onLoadVersion(version)
            }}
            onRollbackVersion={onRollbackVersion}
            rollbacking={submitting}
          />
          <PromptVersionDiff
            open={dialog?.kind === "review"}
            onClose={closeDialog}
            prompt={selectedPrompt}
            title={translate("prompt_action_review")}
            comparePrompt={draft}
            leftStaticLabel="CURRENT"
            rightStaticLabel="DRAFT"
            hideVersionSelectors
          />
          {dialog?.kind === "layer" && (
            <PromptVersionDiff
              open
              onClose={closeDialog}
              prompt={selectedPrompt}
              title={`${promptLayerLabel(
                selectedPrompt.engineVersion
              )} ↔ ${promptLayerLabel(dialog.sibling.engineVersion)}`}
              comparePrompt={dialog.sibling}
              leftStaticLabel={promptLayerLabel(selectedPrompt.engineVersion)}
              rightStaticLabel={promptLayerLabel(dialog.sibling.engineVersion)}
              hideVersionSelectors
            />
          )}
          <PromptDiff
            open={dialog?.kind === "other"}
            onClose={closeDialog}
            prompts={prompts}
            defaultA={promptRowKey(selectedPrompt)}
          />
        </>
      )}
    </Paper>
  )
}
